import { ReactNode, SVGProps } from "react";

import {
  Adjust,
  CheckCircle,
  DropDownFilled,
  EmailAt,
  H1,
  MessagePlus,
  MultiChoice,
  Send,
  TextBlock,
  TextFields,
  ThumbUp,
} from "@livechat/design-system-icons";

import { FormBlockType, IFormBlockMap } from "store/entities/contact-forms/interfaces";

export const GENERIC_REQUIRED_MESSAGE = "Can't be blank";
export const EMAIL_INCORRECT_MESSAGE = "Incorrect email address";

export const SUBMIT_SUCCESS_MESSAGE = "Success!";
export const SUBMIT_ERROR_MESSAGE = "Error 😿";

export const FORM_BUILDER_TABS = {
  GENERAL: "general",
  CUSTOMIZE: "customize",
  PUBLISH: "publish",
};

export const TABS = [
  { id: FORM_BUILDER_TABS.GENERAL, title: "1. General" },
  { id: FORM_BUILDER_TABS.CUSTOMIZE, title: "2. Customize" },
  { id: FORM_BUILDER_TABS.PUBLISH, title: "3. Publish" },
];

// Default labels for each block type. Blocks should also have their own labels based on their value when closed.
export const LABEL_MAP: Record<FormBlockType, string> = {
  header: "Header",
  paragraph: "Description",
  subject: "Subject",
  input: "Text input",
  name: "Name",
  email: "Email",
  textarea: "Message field",
  checkbox: "Multiple-choice list",
  radio: "Single-choice list",
  select: "Dropdown list",
  consent: "Privacy consent",
  options: "More options",
  submit: "Send",
};

// Block types that can have the required property
export const USER_INPUT_BLOCKTYPES = [
  "input",
  "name",
  "email",
  "textarea",
  "select",
  "radio",
  "checkbox",
  "subject",
  "consent",
];

export const BLOCK_DEFAULTS: IFormBlockMap = {
  header: {
    type: "header",
    value: "Contact us",
  },
  paragraph: {
    type: "paragraph",
    value: {
      html: [
        {
          type: "paragraph",
          children: [
            {
              text: "Your thoughts are important to us. To submit comments or questions, please complete the form below.",
            },
          ],
        },
      ],
    },
  },
  input: {
    type: "input",
    value: "Question",
    requiredError: GENERIC_REQUIRED_MESSAGE,
    required: false,
  },
  name: {
    type: "name",
    value: "Full name",
    requiredError: GENERIC_REQUIRED_MESSAGE,
    required: false,
  },
  subject: {
    type: "subject",
    value: "Subject",
    requiredError: GENERIC_REQUIRED_MESSAGE,
    required: false,
  },
  email: {
    type: "email",
    value: "Email address",
    requiredError: GENERIC_REQUIRED_MESSAGE,
    incorrectError: EMAIL_INCORRECT_MESSAGE,
    required: true,
  },
  textarea: {
    type: "textarea",
    value: "Message",
    requiredError: GENERIC_REQUIRED_MESSAGE,
    required: false,
  },
  select: {
    type: "select",
    value: "Question:",
    required: false,
    options: [{ value: "-- Please select an option --" }, { value: "First answer" }, { value: "Second answer" }],
    requiredError: "Select answer",
  },
  radio: {
    type: "radio",
    value: "Question:",
    required: false,
    options: [{ value: "First answer" }, { value: "Second answer" }],
    requiredError: "Select answer",
  },
  checkbox: {
    type: "checkbox",
    value: "Question:",
    required: false,
    options: [{ value: "First answer" }, { value: "Second answer" }],
    requiredError: "Select answer",
  },

  submit: {
    type: "submit",
    value: "Send",
    wide: false,
    sendSuccessLabel: SUBMIT_SUCCESS_MESSAGE,
    sendErrorLabel: SUBMIT_ERROR_MESSAGE,
  },
  options: {
    type: "options",
    sendErrorMessage: "Unfortunately, there was an error processing your request.\nPlease try again later.",
    confirmation: "Thank you! We’ll contact you as soon as possible.",
    poweredByFooter: true,
  },

  consent: {
    type: "consent",
    mode: "disabled",
    value: {
      html: [
        {
          type: "paragraph",
          children: [
            {
              text: "I understand that the controller of my personal data is [your company name] with its registered office in [your business address]. I acknowledge that my personal data shall be processed and transmitted in accordance with the General Data Protection Regulation (GDPR)",
            },
          ],
        },
      ],
    },
    requiredError: "You need to give your consent to process your personal data.",
  },
};

export const SIMPLE_VALUE_BLOCKS = [
  "header",
  "subject",
  "input",
  "name",
  "email",
  "textarea",
  "select",
  "radio",
  "checkbox",
];

export const HTML_VALUE_BLOCKS = ["paragraph"];

// Holds properties for each block type.
// When a block type doesn't have a property, it's assumed to be false.
// A block with no properties just is not unique, not required, and not static.
// For example: Headers are unique but not required.
// Unique: Only one of this type of field is allowed.
// Required: Field is required to be added to the form - it can't be saved without it.
// Static: Field is not allowed to be moved around using drag & drop.
// Hidden from Add: Field is not shown in the Add Fields menu.
export const BLOCK_PROPERTIES: Partial<
  Record<FormBlockType, { unique?: boolean; required?: boolean; static?: boolean; hiddenFromAdd?: boolean }>
> = {
  header: { unique: true },
  subject: { unique: true },
  email: { unique: true, required: true, hiddenFromAdd: true },
  name: { unique: true },
  textarea: { unique: true },
  consent: { unique: true },
  submit: { unique: true, required: true, static: true, hiddenFromAdd: true },
  options: { unique: true, required: true, static: true, hiddenFromAdd: true },
};

export const ICON_MAP: Record<FormBlockType, (props: SVGProps<SVGSVGElement>) => ReactNode> = {
  header: H1,
  paragraph: TextBlock,
  subject: TextFields,
  input: TextFields,
  name: TextFields,
  email: EmailAt,
  textarea: MessagePlus,
  submit: Send,
  options: Adjust,
  checkbox: MultiChoice,
  radio: CheckCircle,
  select: DropDownFilled,
  consent: ThumbUp,
};
